import React, { useRef } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Text } from '@atlaskit/primitives/compiled';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { useDateTimeFormatter } from '@atlassian/jira-issue-format-date/src/main.tsx';
import type { dateTime_issueFieldDateTimeReadviewFull_DateTimeReadView$key as DateTimeReadViewFragment } from '@atlassian/jira-relay/src/__generated__/dateTime_issueFieldDateTimeReadviewFull_DateTimeReadView.graphql';
import { HoverPopover } from '@atlassian/jira-hover-popover/src/ui/index.tsx';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import type { Props } from './types.tsx';

/**
 * DateTimeReadView component lets you view the date and time of a timestammp in the timeZone the current user is in.
 * In Case timeZone is passed as a prop in it adhears to prop timeZone
 *
 * We later need to fireUIAnalytics when an error occurs while formatting the date time
 * also discussions for removing timeZone is available here https://atlassian.slack.com/archives/CFG7SUPJM/p1665391419261039
 *
 * @param props {@link DateTimeReadView}
 * */
export const DateTimeReadView = (props: Props) => {
	const {
		fragmentRef,
		timeZone: overrideTimeZone,
		isDateOnlyFormat = false,
		shouldRenderHoverPopover = false,
	} = props;
	const date = useFragment<DateTimeReadViewFragment>(
		graphql`
			fragment dateTime_issueFieldDateTimeReadviewFull_DateTimeReadView on JiraDateTimePickerField {
				dateTime
			}
		`,
		fragmentRef,
	);
	const { dateTime } = date || {};
	const { formatDateTime, formatDateOnly } = useDateTimeFormatter(overrideTimeZone);

	const contentRef = useRef(null);

	if (!dateTime) {
		return <EmptyFieldText />;
	}

	let content;

	try {
		content = isDateOnlyFormat ? formatDateOnly(dateTime) : formatDateTime(dateTime);
	} catch (err) {
		return <></>;
	}

	if (shouldRenderHoverPopover && dateTime) {
		return (
			<HoverPopover contentRef={contentRef} content={content}>
				{({ ref }) => (
					<Text maxLines={1} ref={mergeRefs(ref, contentRef)}>
						{content}
					</Text>
				)}
			</HoverPopover>
		);
	}

	return content;
};
